import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import supabase from '../utils/supabaseClient';
import katex from 'katex';
import SimpleEditorField from './SimpleEditorField';
import 'katex/dist/katex.min.css';
import './LessonEditor.css';

const STATUS_OPTIONS = ['draft', 'under_review', 'approved'];

const renderLatex = (text) => {
    if (!text) return '';
    let result = text.replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => {
        try {
            return katex.renderToString(latex.trim(), { displayMode: true, throwOnError: false });
        } catch {
            return `$$${latex}$$`;
        }
    });

    result = result.replace(/\$([^$\n]+?)\$/g, (_, latex) => {
        try {
            return katex.renderToString(latex.trim(), { displayMode: false, throwOnError: false });
        } catch {
            return `$${latex}$`;
        }
    });

    return result.replace(/\n/g, '<br/>');
};

const extractMissingColumn = (message = '') => {
    const schemaCacheMatch = message.match(/Could not find the '([^']+)' column/i);
    if (schemaCacheMatch?.[1]) return schemaCacheMatch[1];

    const postgresMatch = message.match(/column\s+"([^"]+)"\s+does not exist/i);
    if (postgresMatch?.[1]) return postgresMatch[1];

    return null;
};

const persistWithSchemaFallback = async ({ table, isEditing, id, payload }) => {
    let currentPayload = { ...payload };
    const removedColumns = [];

    for (let attempt = 0; attempt < 8; attempt += 1) {
        const { data, error } = isEditing
            ? await supabase
                .from(table)
                .update(currentPayload)
                .eq('id', id)
                .select('id')
                .maybeSingle()
            : await supabase
                .from(table)
                .insert([currentPayload])
                .select('id')
                .maybeSingle();

        if (!error) {
            if (!data?.id) {
                throw new Error('Save request completed but no row was returned. Check your lessons INSERT/UPDATE RLS policies.');
            }
            return { removedColumns, savedId: data.id };
        }

        const missingColumn = extractMissingColumn(error?.message || '');
        if (missingColumn && Object.prototype.hasOwnProperty.call(currentPayload, missingColumn)) {
            removedColumns.push(missingColumn);
            delete currentPayload[missingColumn];
            continue;
        }

        throw error;
    }

    return { removedColumns };
};

const LessonEditor = () => {
    const { isDarkTheme } = useTheme();
    const { user, profile } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = Boolean(id);

    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Form fields
    const [title, setTitle] = useState('');
    const [unitId, setUnitId] = useState('');
    const [comments, setComments] = useState('');
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [language, setLanguage] = useState('en');
    const [isTranslated, setIsTranslated] = useState(false);
    const [authorName, setAuthorName] = useState(profile?.display_name || '');
    const [status, setStatus] = useState('draft');

    // Content sections
    const [frontContent, setFrontContent] = useState('');
    const [frontImages, setFrontImages] = useState([]);
    const [hintContent, setHintContent] = useState('');
    const [hintImages, setHintImages] = useState([]);
    const [backContent, setBackContent] = useState('');
    const [backImages, setBackImages] = useState([]);

    // Units list
    const [units, setUnits] = useState([]);

    // Preview toggle
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        const fetchUnits = async () => {
            const { data } = await supabase
                .from('units')
                .select('id, title')
                .order('title', { ascending: true });
            if (data) setUnits(data);
        };
        fetchUnits();
    }, []);

    const loadLesson = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('lessons')
                .select('*')
                .eq('id', id)
                .maybeSingle();

            if (error) throw error;
            if (!data) {
                setMessage({ text: 'Lesson not found.', type: 'error' });
                return;
            }

            setTitle(data.title || '');
            setUnitId(data.unit_id || '');
            setComments(data.comments || '');
            setTags(Array.isArray(data.tags) ? data.tags : []);
            setLanguage(data.language || 'en');
            setIsTranslated(data.is_translated || false);
            setAuthorName(data.author_name || '');
            setStatus(data.status || 'draft');
            setFrontContent(data.front_content || '');
            setFrontImages(Array.isArray(data.front_images) ? data.front_images : []);
            setHintContent(data.hint_content || '');
            setHintImages(Array.isArray(data.hint_images) ? data.hint_images : []);
            setBackContent(data.back_content || '');
            setBackImages(Array.isArray(data.back_images) ? data.back_images : []);
        } catch (err) {
            console.error('[LessonEditor] Load error:', err);
            setMessage({ text: 'Failed to load lesson.', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadLesson();
    }, [loadLesson]);

    const handleAddTag = () => {
        const trimmed = tagInput.trim();
        if (trimmed && !tags.includes(trimmed)) {
            setTags([...tags, trimmed]);
            setTagInput('');
        }
    };

    const handleRemoveTag = (tag) => {
        setTags(tags.filter(t => t !== tag));
    };

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const handleImageUpload = async (e, section) => {
        const file = e.target.files?.[0];
        if (!file || !user) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            setMessage({ text: 'Invalid file type. Only JPEG, PNG, GIF, WebP, and SVG are allowed.', type: 'error' });
            return;
        }

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            setMessage({ text: 'File too large. Maximum size is 5MB.', type: 'error' });
            return;
        }

        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(fileExt)) {
            setMessage({ text: 'Invalid file extension.', type: 'error' });
            return;
        }

        try {
            const fileName = `${user.id}/${Date.now()}.${fileExt}`;
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('exercise-images')
                .upload(fileName, file, {
                    cacheControl: '3600',
                    upsert: true,
                    contentType: file.type,
                });

            if (uploadError) throw uploadError;

            const { data: publicData } = supabase.storage
                .from('exercise-images')
                .getPublicUrl(uploadData?.path || fileName);

            const url = publicData.publicUrl;

            if (section === 'front') setFrontImages(prev => [...prev, url]);
            else if (section === 'hint') setHintImages(prev => [...prev, url]);
            else if (section === 'back') setBackImages(prev => [...prev, url]);
        } catch (err) {
            console.error('[LessonEditor] Image upload error:', err);
            setMessage({ text: 'Failed to upload image.', type: 'error' });
        }
    };

    const handleRemoveImage = (section, index) => {
        if (section === 'front') setFrontImages(prev => prev.filter((_, i) => i !== index));
        else if (section === 'hint') setHintImages(prev => prev.filter((_, i) => i !== index));
        else if (section === 'back') setBackImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ text: '', type: '' });

        try {
            // Only lesson-table columns — no exercise_type, answer_type, options, numeric_answer
            const payload = {
                title,
                unit_id: unitId || null,
                comments,
                tags,
                language,
                is_translated: isTranslated,
                author_name: authorName,
                author_id: user?.id || null,
                status,
                front_content: frontContent,
                front_images: frontImages,
                hint_content: hintContent,
                hint_images: hintImages,
                back_content: backContent,
                back_images: backImages,
                updated_at: new Date().toISOString(),
            };

            if (!isEditing) {
                payload.created_at = new Date().toISOString();
            }

            const { removedColumns, savedId } = await persistWithSchemaFallback({
                table: 'lessons',
                isEditing,
                id,
                payload,
            });

            console.info('[LessonEditor] Lesson saved with id:', savedId);

            setMessage({
                text: `Lesson ${isEditing ? 'updated' : 'created'} successfully!${removedColumns.length > 0 ? ` (Skipped unsupported fields: ${removedColumns.join(', ')})` : ''}`,
                type: 'success',
            });

            if (!isEditing) {
                setTimeout(() => navigate('/admin/exercises?view=lessons'), 1000);
            }
        } catch (err) {
            console.error('[LessonEditor] Save error:', err);
            const raw = err?.message || 'Failed to save lesson.';
            const isRls = raw.toLowerCase().includes('row-level security');
            setMessage({
                text: isRls
                    ? 'Failed to save lesson: database policy blocked this write (RLS). Please update lesson INSERT/UPDATE policies in Supabase.'
                    : raw,
                type: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const renderContentSection = (label, content, setContent, images, sectionKey) => (
        <div className="lesson-content-section">
            <div className="lesson-content-header">
                <h3>{label}</h3>
                <span className="lesson-content-hint">Supports LaTeX: wrap in $...$ or $$...$$</span>
            </div>
            <SimpleEditorField
                value={content}
                onChange={setContent}
                placeholder={`Write ${label.toLowerCase()} content here. Use $...$ for inline LaTeX, $$...$$ for display LaTeX.`}
                minHeight={210}
            />
            <div className="lesson-images-row">
                {images.map((url, index) => (
                    <div key={index} className="lesson-image-thumb">
                        <img src={url} alt={`${label} ${index + 1}`} />
                        <button
                            type="button"
                            className="lesson-image-remove"
                            onClick={() => handleRemoveImage(sectionKey, index)}
                        >
                            ×
                        </button>
                    </div>
                ))}
                <label className="lesson-image-upload">
                    <span>+ Image</span>
                    <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleImageUpload(e, sectionKey)}
                    />
                </label>
            </div>
        </div>
    );

    if (loading) {
        return <div className="admin-loading">Loading...</div>;
    }

    return (
        <div className={`lesson-editor ${isDarkTheme ? 'dark' : 'light'}`}>
            {message.text && (
                <div className={`lesson-message ${message.type}`}>
                    {message.text}
                </div>
            )}

            <form onSubmit={handleSubmit} className="lesson-form">
                <div className="lesson-top-bar">
                    <h2>{isEditing ? 'Edit' : 'Create'} Lesson</h2>
                    <div className="lesson-top-actions">
                        <button
                            type="button"
                            className={`lesson-preview-btn ${showPreview ? 'active' : ''}`}
                            onClick={() => setShowPreview(!showPreview)}
                        >
                            {showPreview ? 'Hide Preview' : 'Preview'}
                        </button>
                        <select
                            className="lesson-status-select"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                        >
                            {STATUS_OPTIONS.map(s => (
                                <option key={s} value={s}>
                                    {s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {showPreview && (
                    <div className="lesson-preview card">
                        <h4>Preview</h4>
                        <div className="lesson-preview-content">
                            <strong>Lesson Content:</strong>
                            <div
                                className="lesson-preview-rendered"
                                dangerouslySetInnerHTML={{ __html: frontContent ? renderLatex(frontContent) : '(empty)' }}
                            />
                            {hintContent && (
                                <>
                                    <strong>Additional Notes:</strong>
                                    <div
                                        className="lesson-preview-rendered"
                                        dangerouslySetInnerHTML={{ __html: renderLatex(hintContent) }}
                                    />
                                </>
                            )}
                            {backContent && (
                                <>
                                    <strong>Summary / Key Takeaways:</strong>
                                    <div
                                        className="lesson-preview-rendered"
                                        dangerouslySetInnerHTML={{ __html: renderLatex(backContent) }}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div className="lesson-grid">
                    <div className="lesson-main-col">
                        {/* Title */}
                        <div className="lesson-field card">
                            <label htmlFor="lesson-title">Lesson Title</label>
                            <input
                                id="lesson-title"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Introduction to Quadratic Equations"
                                required
                            />
                        </div>

                        {/* Main Content */}
                        <div className="card">
                            {renderContentSection('Lesson Content', frontContent, setFrontContent, frontImages, 'front')}
                        </div>

                        {/* Additional Notes / Hints */}
                        <div className="card">
                            {renderContentSection('Additional Notes', hintContent, setHintContent, hintImages, 'hint')}
                        </div>

                        {/* Summary / Key Takeaways */}
                        <div className="card">
                            {renderContentSection('Summary / Key Takeaways', backContent, setBackContent, backImages, 'back')}
                        </div>
                    </div>

                    <div className="lesson-side-col">
                        {/* Metadata */}
                        <div className="lesson-meta card">
                            <h3>Details</h3>

                            <div className="lesson-field">
                                <label htmlFor="lesson-unit-select">Unit</label>
                                <select
                                    id="lesson-unit-select"
                                    value={unitId}
                                    onChange={(e) => setUnitId(e.target.value)}
                                    required
                                >
                                    <option value="">Select a unit</option>
                                    {units.map(u => (
                                        <option key={u.id} value={u.id}>{u.title}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="lesson-field">
                                <label htmlFor="lesson-language-select">Language</label>
                                <select
                                    id="lesson-language-select"
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    <option value="en">English</option>
                                    <option value="fr">French</option>
                                    <option value="it">Italian</option>
                                    <option value="ar">Arabic</option>
                                </select>
                            </div>

                            <div className="lesson-field lesson-checkbox-field">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={isTranslated}
                                        onChange={(e) => setIsTranslated(e.target.checked)}
                                    />
                                    Translated to another language
                                </label>
                            </div>

                            <div className="lesson-field">
                                <label htmlFor="lesson-author-name">Author</label>
                                <input
                                    id="lesson-author-name"
                                    type="text"
                                    value={authorName}
                                    onChange={(e) => setAuthorName(e.target.value)}
                                    placeholder="Author name"
                                />
                            </div>
                        </div>

                        {/* Tags */}
                        <div className="lesson-tags-card card">
                            <h3>Tags</h3>
                            <div className="lesson-tags-list">
                                {tags.map((tag, i) => (
                                    <span key={i} className="lesson-tag">
                                        {tag}
                                        <button type="button" onClick={() => handleRemoveTag(tag)}>×</button>
                                    </span>
                                ))}
                            </div>
                            <div className="lesson-tag-input-row">
                                <input
                                    type="text"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyDown={handleTagKeyDown}
                                    placeholder="Add a tag"
                                />
                                <button type="button" onClick={handleAddTag} className="lesson-tag-add-btn">
                                    Add
                                </button>
                            </div>
                        </div>

                        {/* Comments */}
                        <div className="lesson-comments card">
                            <h3>Comments</h3>
                            <SimpleEditorField
                                value={comments}
                                onChange={setComments}
                                placeholder="Notes, clarifications, teaching objectives..."
                                minHeight={120}
                            />
                        </div>
                    </div>
                </div>

                <div className="lesson-form-actions">
                    <button
                        type="submit"
                        className="admin-action-btn primary"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : (isEditing ? 'Update Lesson' : 'Create Lesson')}
                    </button>
                    <button
                        type="button"
                        className="admin-action-btn secondary"
                        onClick={() => navigate('/admin/exercises?view=lessons')}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default LessonEditor;
